/* POSHRADS — facets.js
   Collection / search filtering (native Shopify GET form, full page loads, no Ajax):
   - Mobile (< 1024px): the filter panel opens as a modal drawer (dialog role, inert page,
     scroll lock, Escape, focus return) — same pattern as the header drawer.
   - Desktop: filter changes submit immediately. Sort changes submit at every size.
   - Empty inputs are disabled on submit so URLs only carry real Shopify filter params.
   One delegated set of listeners, registered once. */
(() => {
  'use strict';

  if (window.poshFacetsReady) return;
  window.poshFacetsReady = true;

  const DESKTOP = window.matchMedia('(min-width: 1024px)');
  let open = null; // { drawer, panel, button, inerted: [] }

  const setInert = (drawer) => {
    const inerted = [];
    let node = drawer;
    while (node && node !== document.body) {
      for (const sibling of node.parentElement.children) {
        if (sibling !== node && !sibling.inert) {
          sibling.inert = true;
          inerted.push(sibling);
        }
      }
      node = node.parentElement;
    }
    return inerted;
  };

  const openDrawer = (button) => {
    const drawer = document.getElementById(button.getAttribute('aria-controls'));
    if (!drawer || DESKTOP.matches) return;
    const panel = drawer.querySelector('[data-facets-panel]');
    drawer.classList.add('is-open');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', panel.dataset.labelledby);
    button.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('scroll-lock');
    open = { drawer, panel, button, inerted: setInert(drawer) };
    (panel.querySelector('button[data-facets-close]') || panel).focus();
  };

  const closeDrawer = (returnFocus) => {
    if (!open) return;
    const { drawer, panel, button, inerted } = open;
    drawer.classList.remove('is-open');
    ['role', 'aria-modal', 'aria-labelledby'].forEach((name) => panel.removeAttribute(name));
    button.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('scroll-lock');
    inerted.forEach((element) => (element.inert = false));
    open = null;
    if (returnFocus) button.focus();
  };

  const submit = (form) => {
    form.querySelectorAll('input, select').forEach((field) => {
      if (field.name && field.value === '' && !field.disabled) {
        field.disabled = true;
        field.setAttribute('data-facets-disabled', '');
      }
    });
    // Sort select lives outside the form (form attribute): include it.
    form.submit();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-facets-open]');
    if (opener) return openDrawer(opener);
    if (event.target.closest('[data-facets-close]')) closeDrawer(true);
  });

  document.addEventListener('keydown', (event) => {
    if (!open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer(true);
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...open.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, summary')].filter(
      (el) => el.getClientRects().length
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('change', (event) => {
    const field = event.target.closest('[data-facets-autosubmit]');
    if (!field) return;
    const form = field.form || document.getElementById(field.getAttribute('form'));
    if (!form) return;
    // In the mobile drawer, wait for "Apply"; sort always applies immediately.
    if (!DESKTOP.matches && field.tagName !== 'SELECT' && open) return;
    submit(form);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (!form) return;
    event.preventDefault();
    submit(form);
  });

  DESKTOP.addEventListener('change', () => closeDrawer(false));

  document.addEventListener('shopify:section:unload', (event) => {
    if (open && event.target.contains(open.drawer)) closeDrawer(false);
  });

  // Fields disabled for a clean submit URL come back disabled from the back/forward cache: re-enable them.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-facets-form] [data-facets-disabled]').forEach((field) => {
      field.disabled = false;
      field.removeAttribute('data-facets-disabled');
    });
  });
})();
